import { DevChatFrontendToolBase } from './DevChatFrontendToolBase';

const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;

function safeStringify(value, limit = 20000) {
  try {
    const json = JSON.stringify(
      value,
      (_key, v) => (typeof v === 'bigint' ? v.toString() : v),
      2
    );
    if (typeof json === 'string' && json.length > limit) {
      return `${json.slice(0, limit)}... (truncated, ${json.length} chars)`;
    }
    return json;
  } catch {
    try {
      const s = String(value);
      return s.length > limit ? `${s.slice(0, limit)}... (truncated)` : s;
    } catch {
      return '[Unserializable value]';
    }
  }
}

export class EvalApprovedFunctionFrontendTool extends DevChatFrontendToolBase {
  static get toolId() {
    return 'eval_approved_function';
  }

  static get description() {
    return 'Propose JavaScript logic and execute it only after user approval (Dev Chat frontend tool, no external input).';
  }

  static get paramsSchema() {
    return {
      type: 'object',
      properties: {
        code: {
          type: 'string',
          description:
            'JavaScript function expression or function body. If body, it is wrapped as async (input)=>{...}.',
        },
        timeoutMs: {
          type: 'number',
          description: 'Optional execution timeout in milliseconds.',
        },
      },
      required: ['code'],
    };
  }

  async onParamsChunk(stream, uiApi) {
    if (!uiApi?.updateToolUi) return;

    const paramsTextSoFar =
      typeof stream?.paramsTextSoFar === 'string' ? stream.paramsTextSoFar : '';
    if (!paramsTextSoFar) return;

    let codeSnippet = '';

    // Best-effort: try full JSON parse first (works once the final chunk arrives).
    try {
      const parsed = JSON.parse(paramsTextSoFar);
      if (parsed && typeof parsed === 'object') {
        if (typeof parsed.code === 'string') codeSnippet = parsed.code;
      }
    } catch (_) {}

    // If we can't parse yet, do a tolerant substring extraction for `"code":"...`
    // so we still get incremental feedback while the model is streaming args.
    if (!codeSnippet) {
      let extractedViaRegex = false;
      const m = paramsTextSoFar.match(/"code"\s*:\s*"/);
      if (m && typeof m.index === 'number') {
        extractedViaRegex = true;
        codeSnippet = paramsTextSoFar.slice(m.index + m[0].length);
      }

      if (codeSnippet && extractedViaRegex) {
        // During streaming we often have raw JSON-escaped content (`\\n`, `\\t`, `\\"`, ...).
        // Convert the most common escape sequences so <pre> renders multiple lines.
        codeSnippet = codeSnippet
          .replace(/\\r/g, '\r')
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\"/g, '"')
          .replace(/\\'/g, "'")
          .replace(/\\\\/g, '\\');
      }
    }

    // Hard-limit preview sizes so we don't nuke the UI with huge partial JSON.
    const MAX_PREVIEW = 12000;
    if (codeSnippet && codeSnippet.length > MAX_PREVIEW) {
      codeSnippet = `${codeSnippet.slice(0, MAX_PREVIEW)}...`;
    }

    uiApi.updateToolUi({
      type: 'approval',
      title: 'eval_approved_function · generating',
      codeSnippet: codeSnippet || '(generating code...)',
      paramsText: '(no input)',
      actions: [],
    });
  }

  async execute(params = {}, context = {}) {
    const code = typeof params?.code === 'string' ? params.code : '';
    const timeoutMsRaw = params?.timeoutMs;
    const timeoutMs =
      typeof timeoutMsRaw === 'number' && Number.isFinite(timeoutMsRaw) && timeoutMsRaw > 0
        ? Math.min(8000, Math.max(10, Math.floor(timeoutMsRaw)))
        : 2000;

    if (code.trim().length === 0) {
      return { approved: false, error: 'Missing code', executed: false };
    }

    const approved = await this.requestApproval(context, {
      title: 'Run proposed JavaScript',
      codeSnippet: code,
      paramsText: '(no input)',
      approveLabel: 'Run',
      denyLabel: 'Deny',
    });

    if (!approved) {
      return { approved: false, denied: true, executed: false };
    }

    const buildFn = () => {
      const trimmed = code.trim();
      try {
        // eslint-disable-next-line no-new-func
        const maybeFn = Function(`return (${trimmed});`)();
        if (typeof maybeFn === 'function') {
          return maybeFn;
        }
      } catch (_) {}

      // Treat `code` as an async function body, wrapped into a zero-arg async function.
      return new AsyncFunction(trimmed);
    };

    let fn;
    try {
      fn = buildFn();
    } catch (err) {
      return { approved: true, executed: false, error: err?.message || String(err) };
    }

    if (typeof fn !== 'function') {
      return {
        approved: true,
        executed: false,
        error: 'Provided code did not evaluate to a function',
      };
    }

    try {
      const resultPromise = Promise.resolve(fn());
      const value = await Promise.race([
        resultPromise,
        new Promise((_, reject) => {
          window.setTimeout(() => {
            reject(new Error(`Execution timed out after ${timeoutMs}ms`));
          }, timeoutMs);
        }),
      ]);
      const returnedUndefined = value === undefined;
      const normalizedValue = returnedUndefined ? null : value;
      const isPrimitive =
        normalizedValue == null ||
        typeof normalizedValue === 'string' ||
        typeof normalizedValue === 'number' ||
        typeof normalizedValue === 'boolean';
      return {
        approved: true,
        executed: true,
        value: isPrimitive ? normalizedValue : safeStringify(normalizedValue),
        returnedUndefined,
      };
    } catch (err) {
      return {
        approved: true,
        executed: false,
        error: err?.message || String(err),
        errorValue: safeStringify(err),
      };
    }
  }

  static formatBubbleSummary(result) {
    if (!result || typeof result !== 'object') return super.formatBubbleSummary(result);
    const r = /** @type {{ approved?: boolean, denied?: boolean, executed?: boolean, error?: string, value?: unknown, returnedUndefined?: boolean }} */ (result);

    if (r.denied || r.approved === false) {
      return 'Eval denied by user';
    }
    if (r.error) {
      return `Eval error: ${r.error}`;
    }
    if (r.executed && r.returnedUndefined) {
      return 'Eval executed successfully (no return value)';
    }
    if (r.executed && 'value' in r) {
      if (typeof r.value === 'string') return `Eval result: ${r.value}`;
      try {
        if (r.value === null) return 'Eval result: null';
        return `Eval result: ${JSON.stringify(r.value)}`;
      } catch {
        return `Eval result: ${String(r.value)}`;
      }
    }
    return super.formatBubbleSummary(result);
  }
}
